import React, { useEffect, useMemo, useState } from "react";

import { BoltIcon, CloseIcon, SpinnerIcon } from "@/components/icons";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { useAppStore } from "@/core/store/useAppStore";

import { adsService } from "../services/adsService";
import { useBoostRecommendation } from "../hooks/useBoostRecommendation";
import { useInvalidateLiveCampaigns } from "../hooks/useLiveCampaigns";
import type {
  BoostRecommendation,
  CampaignType,
  MetaPlacement,
  RecommendationPlatformKey,
} from "../types";
import {
  DRAFT_PLATFORM_ORDER,
  MAX_DURATION,
  MIN_DURATION,
  PLATFORM_ORDER,
  applyDraft,
  buildManualDraft,
  clampDuration,
  draftFromRecommendation,
  recommendationFromManualDraft,
  recommendationToClipboardText,
  recommendationToStandaloneRequest,
  totalFor,
} from "./boostDraft";
import type { EditableDraft, PlatformDraft } from "./boostDraft";

interface BoostModalProps {
  postId: string | null;
  isOpen: boolean;
  onClose: () => void;
  onCampaignCreated?: (campaignIds: string[]) => void;
  /** "ai" runs the Claude agent on open (default). "manual" bypasses
   * the agent entirely and drops straight into the form with defaults. */
  initialMode?: "ai" | "manual";
  /** When true, skip the cache lookup and force a fresh agent run.
   *  Used by the candidate row's "Re-run agent" menu item. */
  forceRerunOnOpen?: boolean;
}

const PLATFORM_LABEL: Record<RecommendationPlatformKey, string> = {
  FACEBOOK: "Facebook",
  INSTAGRAM: "Instagram",
  META: "Meta — dark post (FB + IG)",
};
const PLATFORM_ACCENT: Record<RecommendationPlatformKey, string> = {
  FACEBOOK: "border-blue-200 bg-blue-50/40",
  INSTAGRAM: "border-pink-200 bg-pink-50/40",
  META: "border-indigo-200 bg-indigo-50/40",
};
const PLACEMENT_OPTIONS: MetaPlacement[] = ["FEED", "STORY", "REEL"];
const CAMPAIGN_OBJECTIVES: CampaignType[] = [
  "AWARENESS",
  "TRAFFIC",
  "ENGAGEMENT",
  "CONVERSIONS",
  "REACH",
  "STORE_VISITS",
];
const LOCATION_TYPE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "home", label: "Home / residents" },
  { value: "recent", label: "Recent visitors / tourists" },
];

export const BoostModal: React.FC<BoostModalProps> = ({
  postId,
  isOpen,
  onClose,
  onCampaignCreated,
  initialMode = "ai",
  forceRerunOnOpen = false,
}) => {
  const {
    status,
    run,
    source,
    creation,
    error,
    recommend,
    rerun,
    approve,
    reset,
  } = useBoostRecommendation(postId);
  const invalidateLiveCampaigns = useInvalidateLiveCampaigns();
  const [mode, setMode] = useState<"ai" | "manual">(initialMode);

  useEffect(() => {
    if (isOpen) {
      setMode(initialMode);
    }
  }, [isOpen, initialMode]);

  useEffect(() => {
    if (isOpen && postId && status === "idle" && mode === "ai") {
      // forceRerunOnOpen comes from the "Re-run agent" menu item — it
      // bypasses the cache so the user always gets a fresh agent pass.
      recommend(forceRerunOnOpen ? { force: true } : undefined);
    }
    if (!isOpen) {
      reset();
    }
  }, [isOpen, postId, status, mode, forceRerunOnOpen, recommend, reset]);

  useEffect(() => {
    if (status === "created" && creation) {
      const ids = creation.results
        .filter((r) => r.status === "active" && r.campaignId)
        .map((r) => r.campaignId!);
      const firstCampaign = creation.results.find((r) => r.campaign)?.campaign;
      if (firstCampaign) invalidateLiveCampaigns(firstCampaign.clientId);
      onCampaignCreated?.(ids);
    }
  }, [status, creation, onCampaignCreated, invalidateLiveCampaigns]);

  const recommendation = run?.recommendation ?? null;
  const editable = recommendation?.decision === "recommend";
  const [draft, setDraft] = useState<EditableDraft | null>(null);
  const addToast = useAppStore((s) => s.addToast);
  const [isDarkPostSubmitting, setIsDarkPostSubmitting] = useState(false);

  const canBoostOnMeta = useMemo(() => {
    if (!recommendation) return false;
    const eligibility = recommendation.eligibilityByPlatform;
    if (!eligibility || Object.keys(eligibility).length === 0) {
      // No probe data (post not on Meta, or probe didn't run) — let the
      // backend/Meta surface the failure if the user picks this path.
      // Boost is "API-callable" only if some platform is published on
      // Meta. In the published-but-not-probed case, we permit Boost
      // optimistically.
      const published = recommendation.publishedMetaPlatforms ?? [];
      return published.length > 0;
    }
    return Object.values(eligibility).some((e) => e?.eligible === true);
  }, [recommendation]);

  // Populate draft from Claude (AI) or from defaults (manual)
  useEffect(() => {
    if (!isOpen) {
      setDraft(null);
      return;
    }
    if (draft !== null) return;
    if (mode === "manual") {
      setDraft(buildManualDraft());
    } else if (editable && recommendation) {
      setDraft(draftFromRecommendation(recommendation));
    }
  }, [mode, editable, recommendation, draft, isOpen]);

  const switchToManual = () => {
    reset();
    setMode("manual");
    setDraft(buildManualDraft());
  };

  const draftToFinalRecommendation = (): BoostRecommendation | null => {
    if (!draft) return null;
    if (mode === "manual") return recommendationFromManualDraft(draft);
    if (recommendation) return applyDraft(recommendation, draft);
    return null;
  };

  const submitEdited = () => {
    const finalRec = draftToFinalRecommendation();
    if (finalRec) approve(finalRec);
  };

  const handleDarkPost = async () => {
    if (!postId) return;
    const finalRec = draftToFinalRecommendation();
    if (!finalRec) return;
    setIsDarkPostSubmitting(true);
    try {
      const request = recommendationToStandaloneRequest(postId, finalRec);
      await adsService.createStandaloneAd(request);
      addToast({
        type: "success",
        message:
          "Dark post queued. It'll appear in Live campaigns once Meta accepts the upload.",
        duration: 6000,
      });
      onClose();
    } catch (err) {
      addToast({
        type: "error",
        message:
          err instanceof Error ? err.message : "Failed to queue dark post",
        duration: 6000,
      });
    } finally {
      setIsDarkPostSubmitting(false);
    }
  };

  const handleCopyParams = async () => {
    const finalRec = draftToFinalRecommendation();
    if (!finalRec) return;
    const text = recommendationToClipboardText(finalRec);
    try {
      await navigator.clipboard.writeText(text);
      addToast({
        type: "success",
        message: "Parameters copied — paste into Meta Business Suite.",
        duration: 4000,
      });
    } catch {
      addToast({
        type: "error",
        message: "Couldn't access clipboard. Copy manually from the form.",
        duration: 6000,
      });
    }
  };

  const hasAtLeastOneEnabledPlatform =
    !!draft &&
    Object.values(draft.platforms).some(
      (p) => p?.enabled && (p?.dailyBudget ?? 0) > 0 && (p?.durationDays ?? 0) > 0,
    );
  const manualNeedsCountry =
    mode === "manual" &&
    !!draft &&
    !draft.locations.some((l) => !l.removed && !!l.countryCode);
  const canApprove =
    (mode === "manual"
      ? hasAtLeastOneEnabledPlatform && !manualNeedsCountry
      : editable && hasAtLeastOneEnabledPlatform);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Boost this post"
      size="xl"
      footer={
        <BoostFooter
          status={status}
          canApprove={canApprove}
          canBoostOnMeta={canBoostOnMeta}
          isDarkPostSubmitting={isDarkPostSubmitting}
          onClose={onClose}
          onBoostOnMeta={submitEdited}
          onDarkPost={handleDarkPost}
          onCopy={handleCopyParams}
          onRetry={() => {
            reset();
            setDraft(null);
            recommend({ force: true });
          }}
        />
      }
    >
      <div className="p-6 space-y-4">
        {mode === "ai" && status === "recommending" && (
          <AgentRecommendingState onSkipToManual={switchToManual} />
        )}

        {status === "error" && (
          <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {error ?? "Something went wrong."}
          </div>
        )}

        {status === "created" && creation && (
          <CreationSummary results={creation.results} />
        )}

        {mode === "ai" && recommendation?.decision === "decline" && (
          <DeclineNotice recommendation={recommendation} />
        )}

        {mode === "manual" && draft && status !== "created" && (
          <>
            <div className="rounded border border-blue-100 bg-blue-50 p-3 text-xs text-blue-900">
              <strong>Manual setup</strong> — bypassing the AI agent.
              Country-level targeting only. For city or interest-based
              targeting, close and use &ldquo;Boost&rdquo; instead.
            </div>
            <ManualRecommendationForm draft={draft} onChange={setDraft} />
          </>
        )}

        {mode === "ai" && editable && recommendation && draft && status !== "created" && (
          <>
            {recommendation.campaignActions &&
              recommendation.campaignActions.length > 0 && (
                <CampaignActionsPreview
                  actions={recommendation.campaignActions}
                />
              )}
            <RecommendationForm
              recommendation={recommendation}
              draft={draft}
              onChange={setDraft}
            />
          </>
        )}
      </div>
    </Modal>
  );
};

// ----- Subcomponents ---------------------------------------------------

const BoostFooter: React.FC<{
  status: ReturnType<typeof useBoostRecommendation>["status"];
  canApprove: boolean;
  canBoostOnMeta: boolean;
  isDarkPostSubmitting: boolean;
  onClose: () => void;
  onBoostOnMeta: () => void;
  onDarkPost: () => void;
  onCopy: () => void;
  onRetry: () => void;
}> = ({
  status,
  canApprove,
  canBoostOnMeta,
  isDarkPostSubmitting,
  onClose,
  onBoostOnMeta,
  onDarkPost,
  onCopy,
  onRetry,
}) => {
  if (status === "created") {
    return (
      <div className="flex justify-end gap-2">
        <Button onClick={onClose}>Close</Button>
      </div>
    );
  }

  const isBusy = status === "creating" || isDarkPostSubmitting;
  const apiActionsDisabled = !canApprove || isBusy;

  return (
    <div className="flex flex-wrap items-center justify-between gap-2 w-full">
      <div className="flex gap-2">
        {(status === "error" || status === "ready") && (
          <Button variant="ghost" onClick={onRetry}>
            Rerun agent
          </Button>
        )}
        <Button variant="secondary" onClick={onClose} disabled={isBusy}>
          Cancel
        </Button>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button
          variant="ghost"
          onClick={onCopy}
          disabled={!canApprove}
          title="Copy targeting + budget + caption to clipboard for use in Meta Business Suite"
        >
          Copy parameters
        </Button>
        <Button
          variant="secondary"
          onClick={onDarkPost}
          disabled={apiActionsDisabled}
          isLoading={isDarkPostSubmitting}
          title="Run as a standalone ad (re-uploaded creative — works even when the original post is API-ineligible)"
        >
          Run as dark post
        </Button>
        <Button
          onClick={onBoostOnMeta}
          disabled={apiActionsDisabled || !canBoostOnMeta}
          isLoading={status === "creating"}
          title={
            canBoostOnMeta
              ? "Approve and create a Meta boost campaign on the existing post"
              : "Meta's Ads API marked this post ineligible — use Dark post or Copy parameters instead"
          }
        >
          Boost on Meta
        </Button>
      </div>
    </div>
  );
};

const CampaignActionsPreview: React.FC<{
  actions: NonNullable<BoostRecommendation["campaignActions"]>;
}> = ({ actions }) => (
  <div className="rounded border border-orange-200 bg-orange-50 p-3 text-sm text-orange-900">
    <div className="font-medium mb-1">
      Agent also suggests stopping {actions.length} existing campaign
      {actions.length === 1 ? "" : "s"}
    </div>
    <p className="text-xs text-orange-800 mb-2">
      Approving this recommendation will also apply these actions. Cancel
      and come back if you want to handle them separately.
    </p>
    <ul className="space-y-1 text-xs">
      {actions.map((a, i) => (
        <li key={`${a.campaignId}-${i}`} className="flex gap-2">
          <span className="font-mono text-orange-700 uppercase">
            {a.action}
          </span>
          <span className="font-mono text-orange-600 truncate">
            {a.campaignId.slice(0, 8)}
          </span>
          {a.reason && <span className="text-orange-900">— {a.reason}</span>}
        </li>
      ))}
    </ul>
  </div>
);

const DeclineNotice: React.FC<{ recommendation: BoostRecommendation }> = ({
  recommendation,
}) => {
  const blocked = recommendation.blockedBy;
  const isIneligibilityGate =
    blocked === "not_published_on_meta" || blocked === "meta_ineligible";
  const heading =
    blocked === "not_published_on_meta"
      ? "Not eligible for Meta boost"
      : blocked === "meta_ineligible"
        ? "Meta declined this post"
        : blocked === "existing_live_campaign"
          ? "Not recommended — live campaign already exists"
          : "Not recommended";
  const tone = isIneligibilityGate
    ? "border-gray-200 bg-gray-50 text-gray-900"
    : "border-yellow-200 bg-yellow-50 text-yellow-900";
  const body = isIneligibilityGate ? "text-gray-700" : "text-yellow-800";
  return (
    <div className={`rounded border p-3 text-sm ${tone}`}>
      <div className="font-medium mb-1">{heading}</div>
      {recommendation.reasoning && (
        <p className={`${body} whitespace-pre-line`}>{recommendation.reasoning}</p>
      )}
    </div>
  );
};

const CreationSummary: React.FC<{
  results: import("../types").PlatformCreationResult[];
}> = ({ results }) => {
  const tone = (s: string): string => {
    if (s === "active") return "border-green-200 bg-green-50 text-green-800";
    if (s === "skipped_duplicate") return "border-blue-200 bg-blue-50 text-blue-800";
    return "border-red-200 bg-red-50 text-red-800";
  };
  return (
    <div className="space-y-2">
      {results.map((r) => (
        <div
          key={r.platform}
          className={`rounded border p-3 text-sm ${tone(r.status)}`}
        >
          <div className="font-medium">
            {r.platform}: {r.status}
          </div>
          {r.error && <div className="text-xs mt-1">{r.error}</div>}
        </div>
      ))}
    </div>
  );
};

const RecommendationForm: React.FC<{
  recommendation: BoostRecommendation;
  draft: EditableDraft;
  onChange: (d: EditableDraft) => void;
}> = ({ recommendation, draft, onChange }) => {
  const update = <K extends keyof EditableDraft>(
    key: K,
    value: EditableDraft[K],
  ) => onChange({ ...draft, [key]: value });

  const updatePlatform = (
    platform: RecommendationPlatformKey,
    patch: Partial<PlatformDraft>,
  ) => {
    const current = draft.platforms[platform];
    if (!current) return;
    onChange({
      ...draft,
      platforms: {
        ...draft.platforms,
        [platform]: { ...current, ...patch },
      },
    });
  };

  const togglePlacement = (
    platform: RecommendationPlatformKey,
    placement: MetaPlacement,
  ) => {
    const current = draft.platforms[platform];
    if (!current) return;
    const has = current.placements.includes(placement);
    updatePlatform(platform, {
      placements: has
        ? current.placements.filter((p) => p !== placement)
        : [...current.placements, placement],
    });
  };

  const toggleLocationType = (value: string) => {
    const has = draft.locationTypes.includes(value);
    update(
      "locationTypes",
      has
        ? draft.locationTypes.filter((v) => v !== value)
        : [...draft.locationTypes, value],
    );
  };

  const removeLocation = (i: number) =>
    update(
      "locations",
      draft.locations.map((l, idx) => (idx === i ? { ...l, removed: true } : l)),
    );
  const setRadius = (i: number, radius: number | null) =>
    update(
      "locations",
      draft.locations.map((l, idx) =>
        idx === i ? { ...l, radiusKm: radius } : l,
      ),
    );

  const removeInterest = (i: number) =>
    update(
      "interests",
      draft.interests.map((x, idx) => (idx === i ? { ...x, removed: true } : x)),
    );
  const removeBehavior = (i: number) =>
    update(
      "behaviors",
      draft.behaviors.map((x, idx) => (idx === i ? { ...x, removed: true } : x)),
    );

  const visibleLocations = useMemo(
    () => draft.locations.map((l, idx) => ({ ...l, _idx: idx })).filter((l) => !l.removed),
    [draft.locations],
  );
  const visibleInterests = useMemo(
    () => draft.interests.map((i, idx) => ({ ...i, _idx: idx })).filter((i) => !i.removed),
    [draft.interests],
  );
  const visibleBehaviors = useMemo(
    () => draft.behaviors.map((b, idx) => ({ ...b, _idx: idx })).filter((b) => !b.removed),
    [draft.behaviors],
  );

  const totalBudget = Object.values(draft.platforms).reduce(
    (sum, p) => sum + (p?.enabled ? totalFor(p) : 0),
    0,
  );

  return (
    <div className="space-y-5 text-sm">
      {recommendation.reasoning && (
        <div className="rounded border border-blue-100 bg-blue-50 p-3 text-sm text-blue-900">
          <div className="text-xs font-medium uppercase text-blue-700 mb-1">
            Agent reasoning
          </div>
          <p>{recommendation.reasoning}</p>
          {recommendation.confidence != null && (
            <p className="mt-1 text-xs text-blue-700">
              Confidence: {Math.round(recommendation.confidence * 100)}%
              {recommendation.estimatedReach != null &&
                ` · Estimated reach ${recommendation.estimatedReach.toLocaleString()}`}
            </p>
          )}
        </div>
      )}

      {/* Platform sections — one per recommended platform */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label>Per-platform budgets</Label>
          <div className="text-xs text-gray-600">
            Total: <strong className="text-gray-900">€{totalBudget.toFixed(2)}</strong>
          </div>
        </div>

        {DRAFT_PLATFORM_ORDER.filter((p) => draft.platforms[p]).map(
          (platform) => {
            const block = draft.platforms[platform]!;
            return (
              <PlatformSection
                key={platform}
                platform={platform}
                block={block}
                onToggleEnabled={() =>
                  updatePlatform(platform, { enabled: !block.enabled })
                }
                onDailyBudgetChange={(b) =>
                  updatePlatform(platform, { dailyBudget: b })
                }
                onDurationChange={(d) =>
                  updatePlatform(platform, { durationDays: clampDuration(d) })
                }
                onPlacementToggle={(pl) => togglePlacement(platform, pl)}
              />
            );
          },
        )}

        {DRAFT_PLATFORM_ORDER.every((p) => !draft.platforms[p]) && (
          <div className="rounded border border-dashed border-gray-300 p-3 text-center text-sm text-gray-500">
            Agent didn&apos;t recommend boosting on any platform. The post may not be published anywhere.
          </div>
        )}
      </div>

      {/* Shared targeting */}
      <div className="pt-2 border-t border-gray-200">
        <Label>Shared targeting</Label>
      </div>

      <div>
        <Label>Objective</Label>
        <select
          value={draft.campaignType}
          onChange={(e) => update("campaignType", e.target.value as CampaignType)}
          className={inputClass}
        >
          {CAMPAIGN_OBJECTIVES.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      </div>

      <div>
        <Label>Age range</Label>
        <div className="grid grid-cols-2 gap-4">
          <input
            type="number"
            min="13"
            max="65"
            value={draft.ageMin}
            onChange={(e) => update("ageMin", parseInt(e.target.value) || 13)}
            className={inputClass}
            aria-label="Min age"
          />
          <input
            type="number"
            min="13"
            max="65"
            value={draft.ageMax}
            onChange={(e) => update("ageMax", parseInt(e.target.value) || 65)}
            className={inputClass}
            aria-label="Max age"
          />
        </div>
      </div>

      {recommendation.targeting && (
        <div>
          <Label>
            Geographic scope:{" "}
            <span className="font-medium text-gray-900">
              {recommendation.targeting.geographicScope}
            </span>
          </Label>
          <div className="flex flex-wrap gap-2">
            {LOCATION_TYPE_OPTIONS.map((opt) => {
              const on = draft.locationTypes.includes(opt.value);
              return (
                <label
                  key={opt.value}
                  className={`flex items-center gap-2 rounded-full border px-3 py-1 text-xs cursor-pointer ${
                    on
                      ? "border-blue-500 bg-blue-50 text-blue-900"
                      : "border-gray-200 text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={on}
                    onChange={() => toggleLocationType(opt.value)}
                  />
                  {opt.label}
                </label>
              );
            })}
          </div>
        </div>
      )}

      {visibleLocations.length > 0 && (
        <div>
          <Label>Locations</Label>
          <div className="space-y-2">
            {visibleLocations.map((loc) => (
              <div
                key={loc.metaId}
                className="flex items-center gap-2 rounded border border-gray-200 bg-white px-3 py-2"
              >
                <span className="flex-1 text-gray-900">{loc.name}</span>
                <input
                  type="number"
                  placeholder="radius km"
                  min="1"
                  value={loc.radiusKm ?? ""}
                  onChange={(e) =>
                    setRadius(
                      loc._idx,
                      e.target.value ? parseFloat(e.target.value) : null,
                    )
                  }
                  className="w-24 px-2 py-1 text-xs border border-gray-300 rounded"
                />
                <button
                  type="button"
                  onClick={() => removeLocation(loc._idx)}
                  className="text-gray-400 hover:text-red-500"
                  aria-label="Remove location"
                >
                  <CloseIcon className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {visibleInterests.length > 0 && (
        <div>
          <Label>Interests</Label>
          <ChipList
            items={visibleInterests.map((i) => ({
              id: i.metaId,
              label: i.name,
              _idx: i._idx,
            }))}
            onRemove={(item) => removeInterest(item._idx)}
          />
        </div>
      )}

      {visibleBehaviors.length > 0 && (
        <div>
          <Label>Behaviors</Label>
          <ChipList
            items={visibleBehaviors.map((b) => ({
              id: b.metaId,
              label: b.name,
              _idx: b._idx,
            }))}
            onRemove={(item) => removeBehavior(item._idx)}
          />
        </div>
      )}
    </div>
  );
};

const ManualRecommendationForm: React.FC<{
  draft: EditableDraft;
  onChange: (d: EditableDraft) => void;
}> = ({ draft, onChange }) => {
  const update = <K extends keyof EditableDraft>(
    key: K,
    value: EditableDraft[K],
  ) => onChange({ ...draft, [key]: value });

  const updatePlatform = (
    platform: RecommendationPlatformKey,
    patch: Partial<PlatformDraft>,
  ) => {
    const current = draft.platforms[platform];
    if (!current) return;
    onChange({
      ...draft,
      platforms: {
        ...draft.platforms,
        [platform]: { ...current, ...patch },
      },
    });
  };

  const togglePlacement = (
    platform: RecommendationPlatformKey,
    placement: MetaPlacement,
  ) => {
    const current = draft.platforms[platform];
    if (!current) return;
    const has = current.placements.includes(placement);
    updatePlatform(platform, {
      placements: has
        ? current.placements.filter((p) => p !== placement)
        : [...current.placements, placement],
    });
  };

  const toggleLocationType = (value: string) => {
    const has = draft.locationTypes.includes(value);
    update(
      "locationTypes",
      has
        ? draft.locationTypes.filter((v) => v !== value)
        : [...draft.locationTypes, value],
    );
  };

  const currentCountryCode = draft.locations.find((l) => !l.removed)?.countryCode ?? "";

  const setCountryCode = (code: string) => {
    const trimmed = code.trim().toUpperCase().slice(0, 2);
    if (!trimmed) {
      update("locations", []);
      return;
    }
    update("locations", [
      {
        metaId: trimmed,
        name: trimmed,
        radiusKm: null,
        countryCode: trimmed,
        removed: false,
      },
    ]);
  };

  const totalBudget = Object.values(draft.platforms).reduce(
    (sum, p) => sum + (p?.enabled ? totalFor(p) : 0),
    0,
  );

  return (
    <div className="space-y-5 text-sm">
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label>Per-platform budgets</Label>
          <div className="text-xs text-gray-600">
            Total: <strong className="text-gray-900">€{totalBudget.toFixed(2)}</strong>
          </div>
        </div>
        {PLATFORM_ORDER.filter((p) => draft.platforms[p]).map((platform) => {
          const block = draft.platforms[platform]!;
          return (
            <PlatformSection
              key={platform}
              platform={platform}
              block={block}
              onToggleEnabled={() =>
                updatePlatform(platform, { enabled: !block.enabled })
              }
              onDailyBudgetChange={(b) =>
                updatePlatform(platform, { dailyBudget: b })
              }
              onDurationChange={(d) =>
                updatePlatform(platform, { durationDays: clampDuration(d) })
              }
              onPlacementToggle={(pl) => togglePlacement(platform, pl)}
            />
          );
        })}
      </div>

      <div>
        <Label>Objective</Label>
        <select
          value={draft.campaignType}
          onChange={(e) => update("campaignType", e.target.value as CampaignType)}
          className={inputClass}
        >
          {CAMPAIGN_OBJECTIVES.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      </div>

      <div>
        <Label>Country code (ISO 2-letter, e.g. IE, IT, US)</Label>
        <input
          type="text"
          value={currentCountryCode}
          onChange={(e) => setCountryCode(e.target.value)}
          placeholder="IE"
          maxLength={2}
          className={inputClass + " uppercase"}
        />
        {!currentCountryCode && (
          <p className="text-xs text-red-600 mt-1">
            Required — Meta needs a country to target.
          </p>
        )}
      </div>

      <div>
        <Label>Age range</Label>
        <div className="grid grid-cols-2 gap-4">
          <input
            type="number"
            min="13"
            max="65"
            value={draft.ageMin}
            onChange={(e) => update("ageMin", parseInt(e.target.value) || 13)}
            className={inputClass}
            aria-label="Min age"
          />
          <input
            type="number"
            min="13"
            max="65"
            value={draft.ageMax}
            onChange={(e) => update("ageMax", parseInt(e.target.value) || 65)}
            className={inputClass}
            aria-label="Max age"
          />
        </div>
      </div>

      <div>
        <Label>Audience location types</Label>
        <div className="flex flex-wrap gap-2">
          {LOCATION_TYPE_OPTIONS.map((opt) => {
            const on = draft.locationTypes.includes(opt.value);
            return (
              <label
                key={opt.value}
                className={`flex items-center gap-2 rounded-full border px-3 py-1 text-xs cursor-pointer ${
                  on
                    ? "border-blue-500 bg-blue-50 text-blue-900"
                    : "border-gray-200 text-gray-600 hover:bg-gray-50"
                }`}
              >
                <input
                  type="checkbox"
                  checked={on}
                  onChange={() => toggleLocationType(opt.value)}
                />
                {opt.label}
              </label>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const PlatformSection: React.FC<{
  platform: RecommendationPlatformKey;
  block: PlatformDraft;
  onToggleEnabled: () => void;
  onDailyBudgetChange: (b: number) => void;
  onDurationChange: (d: number) => void;
  onPlacementToggle: (p: MetaPlacement) => void;
}> = ({
  platform,
  block,
  onToggleEnabled,
  onDailyBudgetChange,
  onDurationChange,
  onPlacementToggle,
}) => {
  const total = totalFor(block);
  return (
    <div
      className={`rounded-lg border p-4 space-y-3 ${
        block.enabled ? PLATFORM_ACCENT[platform] : "border-gray-200 bg-gray-50 opacity-70"
      }`}
    >
      <div className="flex items-center justify-between">
        <label className="flex items-center gap-2 font-medium text-gray-900">
          <input
            type="checkbox"
            checked={block.enabled}
            onChange={onToggleEnabled}
          />
          {PLATFORM_LABEL[platform]}
        </label>
        <div className="text-xs text-gray-500">
          Total: <strong className="text-gray-900">€{total.toFixed(2)}</strong>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <label className="flex flex-col gap-1 text-xs">
          <span className="font-medium text-gray-500 uppercase">
            Daily (€)
          </span>
          <input
            type="number"
            min="0"
            step="0.5"
            value={block.dailyBudget}
            disabled={!block.enabled}
            onChange={(e) =>
              onDailyBudgetChange(parseFloat(e.target.value) || 0)
            }
            className="px-2 py-1 text-sm border border-gray-300 rounded text-right disabled:bg-gray-100"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs">
          <span className="font-medium text-gray-500 uppercase">
            Duration (days, {MIN_DURATION}-{MAX_DURATION})
          </span>
          <input
            type="number"
            min={MIN_DURATION}
            max={MAX_DURATION}
            step="1"
            value={block.durationDays}
            disabled={!block.enabled}
            onChange={(e) =>
              onDurationChange(parseInt(e.target.value) || MIN_DURATION)
            }
            className="px-2 py-1 text-sm border border-gray-300 rounded text-right disabled:bg-gray-100"
          />
        </label>
      </div>

      {block.reasoning && (
        <p className="text-xs text-gray-600 italic">{block.reasoning}</p>
      )}

      <div>
        <div className="text-xs font-medium text-gray-500 uppercase mb-1">
          Placements
        </div>
        <div className="flex flex-wrap gap-2">
          {PLACEMENT_OPTIONS.map((placement) => {
            const on = block.placements.includes(placement);
            return (
              <label
                key={placement}
                className={`flex items-center gap-1 rounded border px-2 py-1 text-xs cursor-pointer ${
                  on
                    ? "border-blue-500 bg-white text-blue-900"
                    : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
                } ${!block.enabled ? "opacity-50 pointer-events-none" : ""}`}
              >
                <input
                  type="checkbox"
                  checked={on}
                  onChange={() => onPlacementToggle(placement)}
                  disabled={!block.enabled}
                />
                {placement}
              </label>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const inputClass =
  "w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-hidden transition-colors";

const Label: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="text-xs font-medium text-gray-500 uppercase mb-1">
    {children}
  </div>
);

interface ChipItem {
  id: string;
  label: string;
  _idx: number;
}

const ChipList: React.FC<{
  items: ChipItem[];
  onRemove: (item: ChipItem) => void;
}> = ({ items, onRemove }) => (
  <div className="flex flex-wrap gap-2">
    {items.map((item) => (
      <span
        key={item.id}
        className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-1 text-xs text-gray-700"
      >
        {item.label}
        <button
          type="button"
          onClick={() => onRemove(item)}
          className="text-gray-400 hover:text-red-500"
          aria-label={`Remove ${item.label}`}
        >
          <CloseIcon className="w-3 h-3" />
        </button>
      </span>
    ))}
  </div>
);

// ----- Loading state --------------------------------------------------

/**
 * Boost-recommendation loading panel.
 *
 * The agent loop takes 20-60s typically (5+ tool calls: post context,
 * budget, history, multiple Meta targeting lookups). Showing a
 * single-line "Analyzing..." with a tiny spinner makes that look
 * broken. This panel:
 *   - Large centered spinner + headline
 *   - Rotating step hints so users see progress signals
 *   - Prominent manual-override button (real Button, not an underline)
 *
 * The steps are display-only. Real per-tool-call telemetry would
 * require streaming from the agent; not worth the complexity yet.
 */
const AgentRecommendingState: React.FC<{
  onSkipToManual: () => void;
}> = ({ onSkipToManual }) => {
  const STEPS = [
    "Reading post and client context",
    "Checking available budget",
    "Looking up historical performance",
    "Resolving Meta targeting IDs",
    "Calculating per-platform split",
    "Finalizing recommendation",
  ];
  const [stepIndex, setStepIndex] = useState(0);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  useEffect(() => {
    const stepTimer = setInterval(() => {
      setStepIndex((i) => Math.min(i + 1, STEPS.length - 1));
    }, 6_000);
    const tickTimer = setInterval(() => {
      setElapsedSeconds((s) => s + 1);
    }, 1_000);
    return () => {
      clearInterval(stepTimer);
      clearInterval(tickTimer);
    };
  }, []);

  return (
    <div className="py-8 flex flex-col items-center text-center space-y-5">
      <div className="relative">
        <div className="w-16 h-16 rounded-full border-4 border-blue-100 border-t-blue-500 animate-spin" />
        <div className="absolute inset-0 flex items-center justify-center">
          <BoltIcon className="w-6 h-6 text-blue-500" />
        </div>
      </div>

      <div className="space-y-1">
        <div className="text-base font-semibold text-gray-900">
          Claude is analyzing this post
        </div>
        <div className="text-sm text-gray-600">{STEPS[stepIndex]}…</div>
      </div>

      <div className="flex items-center gap-2 text-xs text-gray-400">
        {STEPS.map((_, i) => (
          <span
            key={i}
            className={`h-1.5 w-5 rounded-full transition-colors ${
              i < stepIndex
                ? "bg-blue-500"
                : i === stepIndex
                  ? "bg-blue-300 animate-pulse"
                  : "bg-gray-200"
            }`}
          />
        ))}
      </div>

      <div className="text-xs text-gray-400">
        {elapsedSeconds < 30
          ? `${elapsedSeconds}s elapsed · usually takes 20-60s`
          : `${elapsedSeconds}s elapsed · taking longer than usual`}
      </div>

      <div className="pt-3 border-t border-gray-100 w-full max-w-sm">
        <Button variant="secondary" onClick={onSkipToManual}>
          Skip AI and set up manually
        </Button>
      </div>
    </div>
  );
};
